import React from 'react';
import type { ReactNode } from 'react';

import { action, computed, observable, observe } from 'mobx';
import type { Lambda } from 'mobx';
import { Provider, inject, observer } from 'mobx-react';

import {
  FacilityCache,
  NetworksCache,
  ProductCache,
  ProductServiceCache,
  RolesCache,
  TeamsCache
} from 'caseflow/src/cache';

import Fallback from 'bz-core-controls/controls/lazy/controls/fallback';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import type { AuthStoreType } from 'bz-stores/lib/auth-store';
import { ConfigObservable } from 'bz-stores/lib/config';
import { LicenseObservable } from 'bz-stores/lib/licence';
import TemplatesStore from 'bz-stores/lib/templates-mobx';

import { DictionaryObservable } from 'bz-utilities/dictionary/dictionary-store';
import Mediator from 'bz-utilities/modules/plugins/register/stores/mediator';

import { ProcessCache } from 'caseflow/src/cache/ProcessCache';

import { MultiDisciplineVisibilityContext } from 'multi-discipline/multi-discipline-visibility-filter';

import type { CacheResponse } from 'tm3-core/api.dtos';
import { CacheRequest, UsedLicenseRequest } from 'tm3-core/api.dtos';
import { ModuleFeatureObservable } from 'tm3-core/controls/module/provider';
import { IsProduct } from 'tm3-core/enums/product-enum';
import { CacheUpdateEvent } from 'tm3-core/events/cache';
import { LicenseCountEvent } from 'tm3-core/events/license-count';
import type ModuleEnum from 'tm3-core/licence';

import ApplicationContext from './application-context';
import ClassTypeStore, { ClassTypeStoreCtx } from './stores/class-type-store';
import CustomerLocationsStore from './stores/customer-locations-store';
import DiscountCodesStore from './stores/discount-codes-store';
import DynamicLabelStore from './stores/dynamic-label-store';
import GroupsStore from './stores/groups-store';
import InventoryStore, { InventoryStoreCtx } from './stores/inventory-store';
import LocationsStore, { LocationsStoreCtx } from './stores/locations-store';
import LookupStore from './stores/lookup-store';
import NestedLookupStore from './stores/nested-lookup-store';
import PermissionsStore, { PermissionsStoreCtx } from './stores/permissions-store';
import PractitionersStore, { PractitionerLocationStore } from './stores/practitioners-store';
import RoomsStore from './stores/rooms-store';
import ServicesStore, { ServicesStoreCtx } from './stores/services-store';
import SkillsStore from './stores/skills-store';
import SuppliersStore from './stores/suppliers-store';
import TagsStore from './stores/tags-store';

interface Props {
  authStore?: any;
  mediator?: Mediator;
  children: ReactNode;
}

export class Cache {
  // not cached yet, just "global"
  app = new ApplicationContext();
  config: Record<string, string>;
  licence: Map<any, any>;
  dictionary: Record<string, any> = {};
  mediator: Mediator = new Mediator();

  // cache items
  permissions: PermissionsStore = new PermissionsStore();
  services: ServicesStore = new ServicesStore();
  locations: LocationsStore = new LocationsStore();
  inventory: InventoryStore = new InventoryStore();
  rooms: RoomsStore = new RoomsStore();
  groups: GroupsStore = new GroupsStore();
  practitioners: PractitionersStore = new PractitionersStore();
  practitionerLocations: PractitionerLocationStore = new PractitionerLocationStore();
  classTypes: ClassTypeStore = new ClassTypeStore();
  lookups: LookupStore = new LookupStore();
  dynamicLabels: DynamicLabelStore = new DynamicLabelStore();
  nestedLookups: NestedLookupStore = new NestedLookupStore();
  customerLocations: CustomerLocationsStore = new CustomerLocationsStore();
  tags: TagsStore = new TagsStore();
  suppliers: SuppliersStore = new SuppliersStore();
  skills: SkillsStore = new SkillsStore();
  discountCodes: DiscountCodesStore = new DiscountCodesStore();
  // Caseflow bits - cheap to init as empty, so hardcoding into tm3-core
  cf_teams: TeamsCache = new TeamsCache();
  cf_process: ProcessCache = new ProcessCache();
  cf_products: ProductCache = new ProductCache();
  cf_p_service: ProductServiceCache = new ProductServiceCache();
  //cf_contracts: ContractsCache = new ContractsCache();
  //cf_reports: CaseflowReportsCache = new CaseflowReportsCache();
  cf_roles: RolesCache = new RolesCache();
  cf_networks: NetworksCache = new NetworksCache();
  cf_facilities: FacilityCache = new FacilityCache();
  authStore: AuthStoreType = null;
  hasAllowedModule: (module: ModuleEnum) => boolean = () => false;

  public checkLicence(product: IsProduct) {
    // if licence isn't loaded just go by app
    if (!this.licence) return true;
    if (!this.licence.has(product)) return true;
    return this.licence.get(product);
  }

  @computed
  get isEClinic(): boolean {
    const lic = this.checkLicence(IsProduct.EClinic);
    return this.app?.EClinic && lic;
  }

  @computed
  get isPPM(): boolean {
    const lic = this.checkLicence(IsProduct.PPM);
    return this.app?.PPM && lic;
  }

  @computed
  get isClick(): boolean {
    const lic = this.checkLicence(IsProduct.Click);
    return this.app?.Click && lic;
  }

  @computed
  get isProntoPortal(): boolean {
    const lic = this.checkLicence(IsProduct.ProntoPortal);
    return this.app?.Pronto && lic;
  }

  @computed
  get isGDS(): boolean {
    const lic = this.checkLicence(IsProduct.GDS);
    return this.app?.GDS && lic;
  }

  @computed
  public get logoPath(): string {
    if (this.isProntoPortal) {
      return '/images/pronto-logo.png';
    } else if (this.app.Caseflow) {
      return '/images/caseflow_text.png';
    } else if (this.isEClinic) {
      return '/images/e-clinic logo.png';
    } else if (this.isGDS) {
      return '/images/gds-logo.png';
    } else if (this.isPPM) {
      return '/images/ppm-logo.svg';
    }

    return '/images/logo.png';
  }

  @computed
  public get logoPathDarkBg(): string {
    if (this.isProntoPortal) {
      return '/images/pronto-logo.png';
    } else if (this.app.Caseflow) {
      return '/images/caseflow_text.png';
    } else if (this.isEClinic) {
      return '/images/e-clinic_white.png';
    } else if (this.isGDS) {
      return '/images/gds-logo.png';
    } else if (this.isPPM) {
      return '/images/ppm-logo_white.svg';
    }

    return '/images/logo.png';
  }

  @computed
  public get productName(): string {
    if (this.isProntoPortal) return 'Pronto Portal';
    if (this.app.Caseflow) return 'Caseflow';
    if (this.isEClinic) return 'E-Clinic';
    if (this.isGDS) return 'GDS';
    if (this.isPPM) return 'PPM';
    if (this.isClick) return 'Click';
    return 'TM3';
  }

  @computed
  get helpUrl(): string {
    return this.isEClinic || this.isPPM ? 'https://help.e-clinic.co.uk' : 'https://help.tm3app.com';
  }
}

const cache = new Cache();
window._tm3cache = cache;
export { cache };
class LoadingStore {
  @observable initialLoad: boolean = true;
}
const store = new LoadingStore();

@inject('authStore', 'mediator')
@observer
class CacheProvider extends React.Component<Props> {
  dispose: Lambda;
  client: JsonServiceClient;

  constructor(props: Props) {
    super(props);
    this.client = new JsonServiceClient('/api');

    cache.mediator = props.mediator!;
    cache.authStore = props.authStore;

    this.dispose = observe(props.authStore, 'authenticated', async (res) => {
      if (!res.newValue) {
        return;
      }

      await this.onConfigure(true);
      await this.getUsedLicenses();
    });
  }

  async componentDidMount() {
    const { mediator } = cache;
    const configure = this.onConfigure.bind(this, false);
    mediator.subscribe<CacheUpdateEvent>(CacheUpdateEvent, configure);
    mediator.subscribe<LicenseCountEvent>(LicenseCountEvent, this.getUsedLicenses);
  }
  staticConfig = {};
  render() {
    cache.config = ConfigObservable.config;
    cache.licence = LicenseObservable.license;
    cache.dictionary = DictionaryObservable.data;
    cache.hasAllowedModule = ModuleFeatureObservable.hasAllowedModule;
    const keyedCache = Object.keys(cache).reduce((prev: any, key: string) => {
      if (!['licence', 'config', 'permissions', 'dictionary'].includes(key)) {
        prev[key + 'Cache'] = cache[key];
      }
      return prev;
    }, {});
    const { authStore } = this.props;
    if (store.initialLoad && authStore.authenticated) {
      return <Fallback />;
    }
    Object.assign(this.staticConfig, ConfigObservable.config);

    const providers = [
      [ServicesStoreCtx.Provider, cache.services] as const,
      [ClassTypeStoreCtx.Provider, cache.classTypes] as const,
      [InventoryStoreCtx.Provider, cache.inventory] as const,
      [LocationsStoreCtx.Provider, cache.locations] as const,
      [PermissionsStoreCtx.Provider, cache.permissions] as const
    ];

    return providers.reduce(
      (acc, x) => {
        const [Provider, value] = x as [React.Provider<any>, any];
        return <Provider value={value}>{acc}</Provider>;
      },
      <Provider
        licence={LicenseObservable.license}
        config={this.staticConfig}
        permissions={cache.permissions}
        {...keyedCache}
      >
        <MultiDisciplineVisibilityContext>{this.props.children}</MultiDisciplineVisibilityContext>
      </Provider>
    );
  }

  @action.bound
  async getUsedLicenses() {
    const res = await this.client.get(new UsedLicenseRequest());
    cache.authStore.usedLicenses = res.result;
  }

  @action.bound
  async onConfigure(initial: boolean) {
    if (initial) {
      store.initialLoad = true;
      TemplatesStore.load();
    }

    let cachedUserId = parseInt(localStorage.getItem('cache:userId')) || -1;
    const authUserId = parseInt(this.props.authStore.user.id) || -1;
    if (authUserId !== cachedUserId) {
      this.clearStorage();
      cache.permissions.reset();
      cachedUserId = authUserId;
    }

    const keys: Record<string, string> = Object.entries(cache).reduce(
      (acc, [key, value]) => {
        if (
          value.version &&
          value.version !== 'undefined' &&
          !['permissions', 'locations', 'services'].includes(key)
        ) {
          acc[key.replace('cache', '')] = value.version;
        }

        return acc;
      },
      {} as Record<string, string>
    );

    const plsCache = await this.client.post(
      new CacheRequest({
        cachedUserId: cachedUserId,
        keys: {
          permissions: cache.permissions.version,
          locations: cache.locations.version,
          services: cache.services.version
        }
      })
    );

    this.processCacheResponse(plsCache.result, initial);

    const allCache = await this.client.post(
      new CacheRequest({
        cachedUserId: cachedUserId,
        keys
      })
    );
    this.processCacheResponse(allCache.result, initial);
    // The below was originally a fix for BZ-694 however is causing
    // a double fetch on the diary by registering the disposers twice
    // meaning its firing the auto run twice. I don't think it is required
    // anymore but leaving in for now until testing confirm it's okay.
    // MMcV 31/08/23
    // DiaryViewStore.onLoad(cachedUserId);
  }

  @action.bound
  processCacheResponse(result: CacheResponse, initial: boolean) {
    const cachedUserId = parseInt(localStorage.getItem('cache:userId')) || -1;
    const clearFirst = result.currentUserId !== cachedUserId.toString();

    if (clearFirst) {
      localStorage.setItem('cache:userId', result.currentUserId);
    }

    if (result.alphabetical) {
      cache.services.orderBy = 'name';
      cache.locations.orderBy = 'name';
      cache.practitioners.orderBy = 'orderId';
      cache.rooms.orderBy = 'description';
      cache.inventory.orderBy = 'description';
      cache.groups.orderBy = 'name';
      cache.classTypes.orderBy = 'name';
    }

    cache.permissions.onLoad('permissions', result);
    const resultKeys = Object.keys(result.keys);
    Object.entries(cache)
      .filter(([key, cacheStore]) => cacheStore.onLoad && resultKeys.includes(key))
      .forEach(([key, cacheStore]) => cacheStore.onLoad(key, result));

    if (initial) {
      store.initialLoad = false;
    }
  }

  clearStorage() {
    Object.keys(cache)
      .filter((x) => cache[x].onClear)
      .forEach((x) => cache[x].onClear());
  }

  componentWillUnmount() {
    const { mediator } = cache;
    const configure = this.onConfigure.bind(this, false);
    mediator.unsubscribe<CacheUpdateEvent>(CacheUpdateEvent, configure);
    this.dispose();
  }
}

export { CacheProvider };
